import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { Checkbox } from '../../../components/ui/Checkbox';

const AttendeeTable = ({ 
  attendees, 
  onCheckIn, 
  onBulkCheckIn, 
  onExport, 
  onSendMessage,
  onViewDetails,
  onProcessRefund,
  selectedAttendees,
  onSelectAttendee,
  onSelectAll
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedAttendees = useMemo(() => {
    if (!sortConfig?.key) return attendees;

    return [...attendees]?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'purchaseDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [attendees, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-foreground" />
      : <Icon name="ArrowDown" size={14} className="text-foreground" />;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'checked-in': { color: 'bg-success text-success-foreground', label: 'Checked In' },
      'not-checked-in': { color: 'bg-muted text-muted-foreground', label: 'Not Checked In' },
      'late': { color: 'bg-warning text-warning-foreground', label: 'Late Arrival' }
    };

    const config = statusConfig?.[status] || statusConfig?.['not-checked-in'];
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': { color: 'bg-success text-success-foreground', label: 'Paid' },
      'pending': { color: 'bg-warning text-warning-foreground', label: 'Pending' },
      'refunded': { color: 'bg-muted text-muted-foreground', label: 'Refunded' }
    };

    const config = statusConfig?.[status] || statusConfig?.['pending'];
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header Actions */}
      <div className="p-4 border-b border-border bg-muted/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Checkbox
              checked={selectedAttendees?.length === attendees?.length && attendees?.length > 0}
              indeterminate={selectedAttendees?.length > 0 && selectedAttendees?.length < attendees?.length}
              onChange={(e) => onSelectAll(e?.target?.checked)}
              label={`${selectedAttendees?.length} selected`}
            />
            {selectedAttendees?.length > 0 && (
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkCheckIn(selectedAttendees)}
                  iconName="UserCheck"
                  iconPosition="left"
                >
                  Bulk Check-in ({selectedAttendees?.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSendMessage(selectedAttendees)}
                  iconName="Mail"
                  iconPosition="left"
                >
                  Send Message
                </Button>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            iconName="Download"
            iconPosition="left"
          >
            Export List
          </Button>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <span className="sr-only">Select</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Attendee</span>
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('ticketTier')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Ticket Tier</span>
                  {getSortIcon('ticketTier')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('purchaseDate')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Purchase Date</span>
                  {getSortIcon('purchaseDate')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-medium text-muted-foreground">Check-in Status</span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-medium text-muted-foreground">Payment</span>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-sm font-medium text-muted-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedAttendees?.map((attendee) => (
              <tr key={attendee?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedAttendees?.includes(attendee?.id)}
                    onChange={(e) => onSelectAttendee(attendee?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {attendee?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{attendee?.name}</div>
                      <div className="text-sm text-muted-foreground">{attendee?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      attendee?.ticketTier === 'VIP' ? 'bg-yellow-500' :
                      attendee?.ticketTier === 'Premium' ? 'bg-blue-500' : 'bg-gray-500'
                    }`} />
                    <span className="text-sm font-medium">{attendee?.ticketTier}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">${attendee?.amount}</div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-foreground">
                    {new Date(attendee.purchaseDate)?.toLocaleDateString()}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(attendee.purchaseDate)?.toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="space-y-2">
                    {getStatusBadge(attendee?.checkInStatus)}
                    {attendee?.checkInTime && (
                      <div className="text-xs text-muted-foreground">
                        {new Date(attendee.checkInTime)?.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="space-y-1">
                    {getPaymentStatusBadge(attendee?.paymentStatus)}
                    <div className="text-xs text-muted-foreground">
                      {attendee?.paymentMethod}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end space-x-2">
                    {attendee?.checkInStatus === 'not-checked-in' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCheckIn(attendee?.id)}
                        iconName="UserCheck"
                      >
                        Check In
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(attendee?.id)}
                      iconName="Eye"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onProcessRefund(attendee?.id)}
                      iconName="RefreshCw"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {sortedAttendees?.map((attendee) => (
          <div key={attendee?.id} className="p-4 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedAttendees?.includes(attendee?.id)}
                  onChange={(e) => onSelectAttendee(attendee?.id, e?.target?.checked)}
                />
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {attendee?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-foreground">{attendee?.name}</div>
                  <div className="text-sm text-muted-foreground">{attendee?.email}</div>
                </div>
              </div>
              {attendee?.checkInStatus === 'not-checked-in' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCheckIn(attendee?.id)}
                  iconName="UserCheck"
                >
                  Check In
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Ticket Tier</div>
                <div className="flex items-center space-x-2 mt-1">
                  <div className={`w-3 h-3 rounded-full ${
                    attendee?.ticketTier === 'VIP' ? 'bg-yellow-500' :
                    attendee?.ticketTier === 'Premium' ? 'bg-blue-500' : 'bg-gray-500'
                  }`} />
                  <span className="font-medium">{attendee?.ticketTier}</span>
                </div>
                <div className="text-muted-foreground">${attendee?.amount}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Purchase Date</div>
                <div className="font-medium mt-1">
                  {new Date(attendee.purchaseDate)?.toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-2">
                {getStatusBadge(attendee?.checkInStatus)}
                {getPaymentStatusBadge(attendee?.paymentStatus)}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewDetails(attendee?.id)}
                  iconName="Eye"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onProcessRefund(attendee?.id)}
                  iconName="RefreshCw"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {attendees?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No attendees found</h3>
          <p className="text-muted-foreground">
            No attendees match your current filters. Try adjusting your search criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default AttendeeTable;